import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  Box, Button, Chip, CircularProgress, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow,
  Tooltip, Typography,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import SettingsIcon from "@material-ui/icons/Settings";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import TableRowSkeleton from "../../../components/TableRowSkeleton";
import MetaAdsModal from "../../../components/MetaAdsModal";
import { useHistory } from "react-router-dom";
import { usePlanPermissions } from "../../../context/PlanPermissionsContext";

const DATE_RANGES = [
  { key: "today", label: "Hoje" },
  { key: "last_7d", label: "7 dias" },
  { key: "last_30d", label: "30 dias" },
  { key: "this_month", label: "Este mês" },
  { key: "last_month", label: "Mês passado" },
];

const STATUS_COLORS = {
  ACTIVE:   { bg: "#f0fdf4", color: "#16a34a", label: "Ativo" },
  PAUSED:   { bg: "#fffbeb", color: "#d97706", label: "Pausado" },
  DELETED:  { bg: "#fef2f2", color: "#dc2626", label: "Deletado" },
  ARCHIVED: { bg: "#f5f5f5", color: "#9e9e9e", label: "Arquivado" },
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    borderBottom: "1px solid #e0e0e0",
    flexWrap: "wrap",
    gap: 12,
    [theme.breakpoints.down("sm")]: { padding: "12px 16px", flexDirection: "column", alignItems: "stretch" },
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 12 },
  headerTitle: { fontSize: "1.25rem", fontWeight: 700, color: "#1a1a1a", [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" } },
  headerSubtitle: { fontSize: "0.8rem", color: "#666" },
  headerRight: { display: "flex", alignItems: "center", gap: 8, [theme.breakpoints.down("sm")]: { justifyContent: "flex-end" } },
  content: { padding: "16px 24px", [theme.breakpoints.down("sm")]: { padding: "12px 12px" } },
  dateBar: { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" },
  dateBtn: {
    borderRadius: 20, textTransform: "none",
    fontSize: 13, padding: "4px 16px", minWidth: 0, fontWeight: 500,
  },
  tableWrapper: {
    backgroundColor: "#fff", borderRadius: 12,
    overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    [theme.breakpoints.down("sm")]: { overflowX: "auto" },
  },
  tableHead: {
    backgroundColor: "var(--sidebar-color, #1e293b)",
    "& th": {
      color: "#cbd5e1", fontWeight: 600, fontSize: "0.78rem",
      textTransform: "uppercase", letterSpacing: "0.05em",
      borderBottom: "none", padding: "13px 14px",
    },
  },
  tableBody: {
    "& td": { padding: "11px 14px", fontSize: "0.875rem", borderBottom: "1px solid #f1f5f9" },
    "& tr:hover": { backgroundColor: "#f8fafc" },
  },
  emptyState: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: "48px 24px", color: "#999",
  },
  setupCard: {
    maxWidth: 520, margin: "40px auto", backgroundColor: "#fff",
    borderRadius: 16, padding: "36px 32px", boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
  },
  helpBox: {
    backgroundColor: "#eff6ff", borderRadius: 8, padding: "12px 16px",
    marginBottom: 20, fontSize: 12, color: "#1e40af", lineHeight: 1.7,
  },
  field: { marginBottom: 16 },
  statCard: {
    backgroundColor: "#fff", borderRadius: 10, padding: "12px 20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.07)", flex: "1 1 160px",
  },
}));

const fmt$ = (v) => `R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
const fmtN = (v) => Number(v).toLocaleString("pt-BR");
const fmtP = (v) => `${Number(v).toFixed(2)}%`;

export default function MetaAds() {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const sidebarColor = theme.palette.primary.main || "#1e293b";

  const [config, setConfig] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState("last_30d");
  const [modalOpen, setModalOpen] = useState(false);

  const { metaAds, loading: planLoading } = usePlanPermissions();
  const history = useHistory();

  useEffect(() => {
    if (!planLoading && !metaAds) {
      toast.warn("Seu plano não inclui este recurso.");
      history.push("/");
    }
  }, [planLoading, metaAds]);

  useEffect(() => { loadConfig(); }, []);

  useEffect(() => {
    if (config) fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, config]);

  const loadConfig = async () => {
    try {
      const { data } = await api.get("/ads/meta/config");
      setConfig(data.configured ? data : false);
    } catch (err) { toastError(err); setConfig(false); }
  };

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ads/meta/campaigns", { params: { datePreset: dateRange } });
      setCampaigns(data.campaigns || []);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    loadConfig();
  };

  const handleRemove = async () => {
    try {
      await api.delete("/ads/meta/config");
      setConfig(false); setCampaigns([]);
      toast.success("Integração removida");
    } catch (err) { toastError(err); }
  };

  const getStatusChip = (status) => {
    const cfg = STATUS_COLORS[status] || { bg: "#f5f5f5", color: "#666", label: status };
    return <Chip size="small" label={cfg.label} style={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, height: 22, fontSize: 11 }} />;
  };

  if (config === null) return (
    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
      <CircularProgress />
      <MetaAdsModal open={modalOpen} onClose={handleModalClose} />
    </Box>
  );

  if (!config) return (
    <Box display="flex" alignItems="center" justifyContent="center" height="100%"
      style={{ flexDirection: "column", gap: 16 }}>
      <MetaAdsModal open={modalOpen} onClose={handleModalClose} />
      <span style={{ fontSize: 48 }}>📘</span>
      <Typography style={{ fontWeight: 700, fontSize: "1.1rem", color: "#1a1a1a" }}>Meta Ads não configurado</Typography>
      <Typography style={{ fontSize: 13, color: "#666" }}>
        Configure suas credenciais em Configurações → Canais → Adicionar Conexão
      </Typography>
    </Box>
  );

  const totalSpend = campaigns.reduce((s, c) => s + c.spend, 0);
  const totalImpressions = campaigns.reduce((s, c) => s + c.impressions, 0);
  const totalClicks = campaigns.reduce((s, c) => s + c.clicks, 0);

  return (
    <Box className={classes.root} style={{ "--sidebar-color": sidebarColor }}>
      <MetaAdsModal open={modalOpen} onClose={handleModalClose} />
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <span style={{ fontSize: 28 }}>📘</span>
          <Box>
            <Typography className={classes.headerTitle}>Meta Ads</Typography>
            <Typography className={classes.headerSubtitle}>Conta: act_{config.adAccountId} · {campaigns.length} campanhas</Typography>
          </Box>
        </Box>
        <Box className={classes.headerRight}>
          <Tooltip title="Atualizar"><IconButton size="small" onClick={fetchCampaigns} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Reconfigurar"><IconButton size="small" onClick={() => setModalOpen(true)}><SettingsIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Remover integração"><IconButton size="small" onClick={handleRemove} style={{ color: "#ef4444" }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
        </Box>
      </Box>

      <Box className={classes.content}>
        <Box display="flex" style={{ gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
          {[
            { label: "Gasto total", value: fmt$(totalSpend), color: "#dc2626" },
            { label: "Impressões", value: fmtN(totalImpressions), color: "#2563eb" },
            { label: "Cliques", value: fmtN(totalClicks), color: "#16a34a" },
          ].map(card => (
            <Box key={card.label} className={classes.statCard}>
              <Typography style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>{card.label}</Typography>
              <Typography style={{ fontSize: 22, fontWeight: 700, color: card.color }}>{card.value}</Typography>
            </Box>
          ))}
        </Box>

        <Box className={classes.dateBar}>
          {DATE_RANGES.map(d => (
            <Button key={d.key} className={classes.dateBtn} size="small"
              variant={dateRange === d.key ? "contained" : "outlined"}
              color={dateRange === d.key ? "primary" : "default"}
              onClick={() => setDateRange(d.key)}
            >{d.label}</Button>
          ))}
        </Box>

        <Box className={classes.tableWrapper}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Campanha</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Gasto</TableCell>
                <TableCell align="right">Impressões</TableCell>
                <TableCell align="right">Alcance</TableCell>
                <TableCell align="right">Cliques</TableCell>
                <TableCell align="right">CTR</TableCell>
                <TableCell align="right">CPC</TableCell>
                <TableCell align="right">CPM</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={classes.tableBody}>
              {loading ? <TableRowSkeleton columns={9} /> : campaigns.length === 0 ? (
                <TableRow><TableCell colSpan={9}>
                  <Box className={classes.emptyState}><Typography>Nenhuma campanha no período selecionado</Typography></Box>
                </TableCell></TableRow>
              ) : campaigns.map(c => (
                <TableRow key={c.id}>
                  <TableCell>
                    <Typography style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</Typography>
                    {c.objective && <Typography variant="caption" style={{ color: "#94a3b8" }}>{c.objective.replace(/_/g, " ")}</Typography>}
                  </TableCell>
                  <TableCell align="center">{getStatusChip(c.status)}</TableCell>
                  <TableCell align="right" style={{ fontWeight: 600, color: "#dc2626" }}>{fmt$(c.spend)}</TableCell>
                  <TableCell align="right">{fmtN(c.impressions)}</TableCell>
                  <TableCell align="right">{fmtN(c.reach)}</TableCell>
                  <TableCell align="right">{fmtN(c.clicks)}</TableCell>
                  <TableCell align="right">{fmtP(c.ctr)}</TableCell>
                  <TableCell align="right">{fmt$(c.cpc)}</TableCell>
                  <TableCell align="right">{fmt$(c.cpm)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
}
